import { useState, useCallback, startTransition } from 'react';
import { Company, Group, IndustryType, Unit, UnitFilterData } from '@/types';
import { cache, invalidateFleetCaches } from '@/lib/cache';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import {
  FilterContext,
  CACHE_KEY_BASE,
  getUserCacheKey,
  type SavedFilters,
} from './FilterContext';
import { useFilterGroupDerivations } from './filterProvider/useFilterGroupDerivations';
import { useFilterAnalytics } from './filterProvider/useFilterAnalytics';
import { useFilterCacheRestore, useFilterSelectionSync } from './filterProvider/useFilterCachePersistence';
import { useFleetDataLoaders } from './filterProvider/useFleetDataLoaders';

/**
 * Initializes and provides global company filter state and related actions to descendant components.
 *
 * The provider exposes companies, groups, units, selection state (companies, groups, models, statuses, industry types),
 * derived hierarchical group data, and mutator/helper functions (toggleGroupWithCascade, flushCache, save/restore filters, etc.)
 * via FilterContext so child components can read and modify global filter state.
 *
 * The heavy lifting lives in ./filterProvider/* hooks (ISS-030, file-length split),
 * each called at the position its inline effects used to occupy so
 * effect-registration order is unchanged:
 *   useFilterCacheRestore  → one-shot restore from user-scoped cache (before loaders)
 *   useFleetDataLoaders    → companies → groups → units fetch effects
 *   useFilterSelectionSync → per-selection localStorage writes (after loaders)
 *   useFilterGroupDerivations → pure group tree/maps + selection helpers
 *   useFilterAnalytics     → trackFilterChanged effects (last)
 *
 * @returns A context provider element that renders the given `children` and supplies the filter state and actions.
 */
export function FilterProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { rbacReady } = useRBAC();

  // CRITICAL: Don't initialize from cache until we know who the user is.
  // This prevents showing User A's data to User B on shared devices.
  // The ProtectedRoute already guards rendering until rbacReady,
  // but we double-guard here for defense in depth.

  // Initialize selected IDs - EMPTY until rbacReady (will be populated from user-specific cache)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>(Object.keys(MODEL_LABELS));
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['online', 'idle', 'offline', 'unknown']);
  // Initialize with all industry types selected (same pattern as models/statuses)
  // Cache restoration will override this with user's saved preferences
  const [selectedIndustryTypes, setSelectedIndustryTypes] = useState<IndustryType[]>(INDUSTRY_TYPE_ORDER);

  // Centralized units state - pages push units here for global filter access
  // Accepts either full Unit[] or minimal UnitFilterData[] for filter calculations
  const [units, setUnitsInternal] = useState<(Unit | UnitFilterData)[]>([]);

  // Wrapped setUnits that uses startTransition to prevent hook count mismatches
  // during rapid re-renders when external components update units state
  const setUnits = useCallback((newUnits: (Unit | UnitFilterData)[]) => {
    startTransition(() => {
      setUnitsInternal(newUnits);
    });
  }, []);

  // Saved filters for restoration (used by cluster tooltip quick filters)
  const [savedFiltersForRestore, setSavedFiltersForRestore] = useState<SavedFilters | null>(null);

  // Sidebar expand-all trigger (for cluster tooltip zoom-to actions)
  const [shouldExpandAll, setShouldExpandAll] = useState(false);

  // Initialize data as EMPTY - will be populated once rbacReady
  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  const [isLoading, setIsLoading] = useState(true); // Start TRUE until rbacReady
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Bumped by refreshFleetData()/flushCache() to re-run the loader effects below
  // after their caches have been dropped.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Restore user-scoped filter state from cache once rbacReady. Registered
  // BEFORE the loaders so their synchronous cache reads and the sync effects
  // keep their original ordering.
  useFilterCacheRestore({
    user,
    rbacReady,
    setIsLoading,
    setCompanies,
    setGroups,
    setUnitsInternal,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedModels,
    setSelectedStatuses,
    setSelectedIndustryTypes,
  });

  // Fetch companies → groups → units. Registered AFTER restore, BEFORE sync.
  useFleetDataLoaders({
    user,
    rbacReady,
    companies,
    selectedCompanyIds,
    hasInitialLoad,
    refreshNonce,
    setCompanies,
    setGroups,
    setUnitsInternal,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedIndustryTypes,
    setHasInitialLoad,
  });

  // Persist each selection to user-scoped localStorage. Registered AFTER the
  // loaders, matching the original ordering.
  useFilterSelectionSync({
    user,
    hasInitialLoad,
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    selectedStatuses,
    selectedIndustryTypes,
  });

  // NOTE: Removed auto-select safeguards for industry types, models, and companies.
  // Users can now clear all selections to see empty state (no data visible).
  // Use "Reset Filters" or filter dropdowns to re-select items.
  // This enables intentional filtering: if a categorization is not selected,
  // units connected to it should not show up.

  // Derived group data + group selection helpers (pure; no effects).
  const {
    groupTree,
    companyGroupsMap,
    groupDescendantsMap,
    toggleGroupWithCascade,
    getGroupSelectionState,
    buildHierarchicalGroups,
  } = useFilterGroupDerivations(groups, companies, selectedGroupIds, setSelectedGroupIds);

  // Flush cache and force reload
  const flushCache = useCallback(() => {
    console.log('🗑️ [FilterContext] Flushing ALL caches (localStorage + IndexedDB)');

    const userId = user?.id;

    // Clear localStorage cache (user-specific keys)
    if (userId) {
      try {
        const keysToRemove = [
          getUserCacheKey(CACHE_KEY_BASE.companies, userId),
          getUserCacheKey(CACHE_KEY_BASE.groups, userId),
          getUserCacheKey(CACHE_KEY_BASE.units, userId),
          getUserCacheKey(CACHE_KEY_BASE.unitsTimestamp, userId),
          getUserCacheKey(CACHE_KEY_BASE.selectedCompanyIds, userId),
          getUserCacheKey(CACHE_KEY_BASE.selectedGroupIds, userId),
          getUserCacheKey(CACHE_KEY_BASE.selectedModels, userId),
          getUserCacheKey(CACHE_KEY_BASE.selectedStatuses, userId),
          getUserCacheKey(CACHE_KEY_BASE.timestamp, userId),
        ].filter(Boolean) as string[];

        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('✅ [FilterContext] localStorage cleared for user:', userId);
      } catch (error) {
        console.warn('Failed to flush cache from localStorage:', error);
      }
    }

    // CRITICAL: Also clear IndexedDB cache used by staleWhileRevalidate
    // Without this, getCompanies() returns stale cached data from IndexedDB!
    cache.clear().then(() => {
      console.log('✅ [FilterContext] IndexedDB cache cleared');
    }).catch(error => {
      console.warn('Failed to clear IndexedDB cache:', error);
    });

    // CRITICAL: Clear state variables to force reload
    // Without this, the useEffect sees companies.length > 0 and skips reload
    setCompanies([]);
    setGroups([]);
    setUnits([]);
    setSelectedCompanyIds([]);
    setSelectedGroupIds([]);

    // Trigger reload. The nonce also releases the once-per-cycle guards on the
    // loader effects, which would otherwise treat this as an already-served load.
    setHasInitialLoad(false);
    setRefreshNonce(n => n + 1);
  }, [user?.id, setUnits]);

  /**
   * Pull fleet structure again after a write, without disturbing the user's view.
   *
   * Unlike flushCache() this keeps companies/groups/units on screen and keeps the
   * current filter SELECTIONS — it only drops the caches that would otherwise keep
   * serving pre-mutation data, then re-runs the loaders. Newly created groups and
   * companies are folded into the selection by the loaders themselves.
   */
  const refreshFleetData = useCallback(async () => {
    const userId = user?.id;

    // Drop the localStorage freshness gate so the companies effect re-fetches.
    // The companies/groups PAYLOAD mirrors are deliberately left in place: the
    // loaders diff against them to tell a brand-new entity from one the user
    // deliberately unticked.
    if (userId) {
      const timestampKey = getUserCacheKey(CACHE_KEY_BASE.timestamp, userId);
      const unitsTimestampKey = getUserCacheKey(CACHE_KEY_BASE.unitsTimestamp, userId);
      try {
        if (timestampKey) localStorage.removeItem(timestampKey);
        if (unitsTimestampKey) localStorage.removeItem(unitsTimestampKey);
      } catch (error) {
        console.warn('[FilterContext] Failed to clear cache timestamps:', error);
      }
    }

    // IndexedDB is the layer that actually suppresses the network call.
    await invalidateFleetCaches();

    setRefreshNonce(n => n + 1);
  }, [user?.id]);

  // Save current filters for restoration (used by cluster tooltip quick filters)
  const saveFiltersForRestore = useCallback(() => {
    setSavedFiltersForRestore({
      companyIds: [...selectedCompanyIds],
      groupIds: [...selectedGroupIds],
      industryTypes: [...selectedIndustryTypes],
      models: [...selectedModels],
      statuses: [...selectedStatuses],
    });
  }, [selectedCompanyIds, selectedGroupIds, selectedIndustryTypes, selectedModels, selectedStatuses]);

  // Restore previously saved filters
  const restoreSavedFilters = useCallback(() => {
    if (savedFiltersForRestore) {
      setSelectedCompanyIds(savedFiltersForRestore.companyIds);
      setSelectedGroupIds(savedFiltersForRestore.groupIds);
      setSelectedIndustryTypes(savedFiltersForRestore.industryTypes);
      setSelectedModels(savedFiltersForRestore.models);
      setSelectedStatuses(savedFiltersForRestore.statuses);
      setSavedFiltersForRestore(null); // Clear saved state after restoring
    }
  }, [savedFiltersForRestore]);

  // Whether we have saved filters that can be restored
  const hasSavedFilters = savedFiltersForRestore !== null;

  // Track filter changes after initial load completes. Registered last, matching
  // the original position of the inline tracking effects.
  useFilterAnalytics({
    isLoading,
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    selectedIndustryTypes,
    companiesCount: companies.length,
    groupsCount: groups.length,
  });

  return (
    <FilterContext.Provider
      value={{
        selectedCompanyIds: selectedCompanyIds,
        selectedGroupIds,
        selectedIndustryTypes,
        selectedModels,
        selectedStatuses,
        companies: companies,
        groups,
        groupTree,
        companyGroupsMap,
        groupDescendantsMap,
        units,
        setUnits,
        setSelectedCompanyIds: setSelectedCompanyIds,
        setSelectedGroupIds,
        setSelectedIndustryTypes,
        setSelectedModels,
        setSelectedStatuses,
        toggleGroupWithCascade,
        getGroupSelectionState,
        flushCache,
        refreshFleetData,
        isLoading,
        hasSavedFilters,
        saveFiltersForRestore,
        restoreSavedFilters,
        buildHierarchicalGroups,
        shouldExpandAll,
        setShouldExpandAll,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}
